import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'

/**
 * Path to Victory — the arithmetic every other module was feeding.
 *
 * ## The projection is a GET and is never cached
 *
 * It is derived from geography, the register and the tally, all of which move daily. A stored
 * answer here would be the one number on this platform that looks authoritative while being
 * out of date, which is the failure the whole feature exists to prevent.
 */

export interface VictoryPlan {
  id: string
  electionId: string
  seatType: string
  unitId: string
  unitPath: string
  name: string
  thresholdKind: 'PLURALITY' | 'SIMPLE_MAJORITY' | 'CUSTOM'
  customThresholdPct: number | null
  contestSize: number
  assumedTurnoutPct: number
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED'
  notes: string | null
  createdAt: string
}

export interface VictoryUnitRow {
  unitId: string
  unitName: string
  unitPath: string
  registered: number
  projectedTurnout: number
  projectedVotes: number
  targetShare: number
  gap: number
  assessedCoveragePct: number
  turnoutPctUsed: number
  supportPctUsed: number
  turnoutMeasured: boolean
  overridden: boolean
  unreadable: boolean
}

export interface VictoryProjection {
  planId: string
  planName: string
  thresholdKind: string
  registered: number
  projectedTurnout: number
  projectedVotes: number
  projectedVotesLow: number
  projectedVotesHigh: number
  targetVotes: number
  gap: number
  /** True for PLURALITY: the target is the least it can take, not the amount. */
  targetIsFloor: boolean
  assessedCoveragePct: number
  coverageVerdict: 'SUFFICIENT' | 'THIN' | 'NOT_DECISION_GRADE'
  /** False below the coverage floor. The page must not present the gap as a finding. */
  decisionGrade: boolean
  credibilityNote: string
  unreadableStations: number
  unreadableRegistered: number
  stationsWithMeasuredTurnout: number
  stationsTotal: number
  sensitivity: {
    worstCase: number
    bestCase: number
    winsInWorstCase: boolean
    winsInBestCase: boolean
  } | null
  units: VictoryUnitRow[]
}

export interface VictoryBacktest {
  planId: string
  electionId: string
  stationsScored: number
  assumedTurnoutPct: number
  meanAbsoluteErrorPp: number
  worstErrorPp: number
  worstStation: string
  projectedCast: number
  actualCast: number
  castError: number
  caveat: string
}

export interface VictoryAssumption {
  id: string
  unitId: string
  unitPath: string
  assumedTurnoutPct: number | null
  assumedSupportPct: number | null
  rationale: string
  createdAt: string
}

export const listVictoryPlans = (election?: string) =>
  api.get<VictoryPlan[]>(`/victory/plans${qs({ election })}`)

export const getProjection = (planId: string) =>
  api.get<VictoryProjection>(`/victory/plans/${planId}/projection`)

/** Omit `election` and the server picks the most recent completed one that is not this plan's. */
export const getBacktest = (planId: string, election?: string) =>
  api.get<VictoryBacktest>(`/victory/plans/${planId}/backtest${qs({ election })}`)

export const listAssumptions = (planId: string) =>
  api.get<VictoryAssumption[]>(`/victory/plans/${planId}/assumptions`)

/** Requires `victory:manage`. The rationale is enforced server-side at ten characters. */
export const setAssumption = (
  planId: string,
  body: {
    unitId: string
    assumedTurnoutPct?: number | null
    assumedSupportPct?: number | null
    rationale: string
  },
) => api.post<VictoryAssumption>(`/victory/plans/${planId}/assumptions`, body)

export const takeSnapshot = (planId: string, note?: string) =>
  api.post<unknown>(`/victory/plans/${planId}/snapshots`, { note: note ?? null })
